import React, { useState, useEffect, useMemo, useRef } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Divider,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogContent,
  Tooltip
} from "@material-ui/core";
import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import { purple, green, teal, orange, blue } from "@material-ui/core/colors";
import {
  Print as PrintIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  WhatsApp as WhatsAppIcon,
  PictureAsPdf as PdfIcon,
  Dashboard as DashboardIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  Event as EventIcon,
  Comment as CommentIcon,
  TrendingUp as TrendingUpIcon,
  OpenInNew as OpenInNewIcon
} from "@material-ui/icons";
import api from "../../services/api";
import { toast } from "react-toastify";
import ClienteResumoPrintTemplate from "./PrintTemplate";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    "@media print": {
      padding: 0,
      margin: 0,
    },
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    "@media print": {
      boxShadow: "none",
      padding: theme.spacing(2),
      margin: 0,
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    "@media print": {
      justifyContent: "center",
      borderBottom: "2px solid #333",
      paddingBottom: theme.spacing(2),
    },
  },
  printButton: {
    marginLeft: "auto",
    "@media print": {
      display: "none !important",
    },
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    "@media print": {
      color: "#333",
      fontSize: "24pt",
      marginBottom: theme.spacing(1),
      textAlign: "center",
    },
  },
  subtitle: {
    fontWeight: "bold",
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    "@media print": {
      color: "#333",
      fontSize: "14pt",
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      borderBottom: "1px solid #ccc",
      paddingBottom: theme.spacing(0.5),
    },
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "@media print": {
      fontSize: "10pt",
      marginBottom: theme.spacing(0.5),
    },
  },
  infoIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
    "@media print": {
      color: "#666",
      fontSize: "12pt",
    },
  },
  section: {
    marginBottom: theme.spacing(3),
    "@media print": {
      marginBottom: theme.spacing(2),
      pageBreakInside: "avoid",
    },
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& th": {
      color: "#fff",
      fontWeight: "bold",
    },
    "@media print": {
      backgroundColor: "#333 !important",
      "& th": {
        color: "#fff !important",
        fontSize: "10pt",
        padding: "8px",
        border: "1px solid #333",
      },
    },
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 400,
  },
  chip: {
    margin: theme.spacing(0.5),
    "@media print": {
      border: "1px solid #333",
      backgroundColor: "#f0f0f0",
      fontSize: "8pt",
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    "@media print": {
      margin: theme.spacing(1, 0),
      border: "none",
      borderTop: "1px dashed #ccc",
    },
  },
  contactItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(1),
    "@media print": {
      border: "1px solid #ddd",
      borderRadius: 0,
      backgroundColor: "#fff",
      padding: theme.spacing(0.5),
      marginBottom: theme.spacing(0.5),
      pageBreakInside: "avoid",
    },
  },
  addressBox: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(2),
    "@media print": {
      border: "1px solid #ddd",
      borderRadius: 0,
      backgroundColor: "#fff",
      padding: theme.spacing(1),
      marginBottom: theme.spacing(1),
      pageBreakInside: "avoid",
    },
  },
  printOnly: {
    display: "none",
    "@media print": {
      display: "block",
      textAlign: "center",
      marginBottom: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      borderBottom: "2px solid #333",
    },
  },
  noPrint: {
    "@media print": {
      display: "none !important",
    },
  },
}));

const ClienteResumo = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const history = useHistory();
  const { clienteId } = useParams();
  const [loading, setLoading] = useState(true);
  const [resumoData, setResumoData] = useState(null);
  const [kanbanCards, setKanbanCards] = useState([]);
  const [kanbanCardsLoading, setKanbanCardsLoading] = useState(false);
  const [kanbanSortBy, setKanbanSortBy] = useState("boardName");
  const [kanbanSortDirection, setKanbanSortDirection] = useState("asc");
  const [kanbanPage, setKanbanPage] = useState(0);
  const [kanbanRowsPerPage, setKanbanRowsPerPage] = useState(10);
  const printRef = useRef();

  useEffect(() => {
    fetchResumoData();
  }, [clienteId]);

  const fetchResumoData = async () => {
    setLoading(true);
    setKanbanCardsLoading(true);
    try {
      // [Copilot | 2026-04-20] Busca o resumo e os cards detalhados para acesso direto ao card correto.
      const [resumoResult, kanbanCardsResult] = await Promise.allSettled([
        api.get(`/clientes/${clienteId}/resumo`),
        api.get(`/kanban/cards/cliente/${clienteId}`)
      ]);

      if (resumoResult.status === "fulfilled") {
        setResumoData(resumoResult.value.data);
      } else {
        throw resumoResult.reason;
      }

      if (kanbanCardsResult.status === "fulfilled") {
        const cardsData = kanbanCardsResult.value?.data;
        setKanbanCards(Array.isArray(cardsData) ? cardsData : []);
      } else {
        console.error("Erro ao buscar cards do kanban:", kanbanCardsResult.reason);
        setKanbanCards([]);
      }
    } catch (error) {
      console.error("Erro ao buscar resumo do cliente:", error);
      toast.error("Erro ao carregar resumo do cliente");
      setKanbanCards([]);
    } finally {
      setLoading(false);
      setKanbanCardsLoading(false);
    }
  };

  const handleOpenKanbanCard = (card) => {
    if (!card?.id || !card?.boardId || !card?.columnId) {
      return;
    }

    history.push(`/kanban-board?boardId=${card.boardId}&columnId=${card.columnId}&cardId=${card.id}`);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return "-";
    }

    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(value));
  };

  const getSortableText = (value) => String(value || "").toLocaleLowerCase("pt-BR");

  // [Copilot | 2026-04-20] Ordena cards por coluna selecionada e pagina o resultado para listas grandes.
  const sortedKanbanCards = useMemo(() => {
    const cards = [...kanbanCards];

    cards.sort((a, b) => {
      if (kanbanSortBy === "valor") {
        const aValid = a?.valor !== null && a?.valor !== undefined && !Number.isNaN(Number(a.valor));
        const bValid = b?.valor !== null && b?.valor !== undefined && !Number.isNaN(Number(b.valor));

        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;

        const aValue = Number(a.valor);
        const bValue = Number(b.valor);
        return kanbanSortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      const aValue = getSortableText(a?.[kanbanSortBy]);
      const bValue = getSortableText(b?.[kanbanSortBy]);
      const comparison = aValue.localeCompare(bValue, "pt-BR", { sensitivity: "base" });

      return kanbanSortDirection === "asc" ? comparison : -comparison;
    });

    return cards;
  }, [kanbanCards, kanbanSortBy, kanbanSortDirection]);

  const paginatedKanbanCards = useMemo(() => {
    const start = kanbanPage * kanbanRowsPerPage;
    const end = start + kanbanRowsPerPage;
    return sortedKanbanCards.slice(start, end);
  }, [sortedKanbanCards, kanbanPage, kanbanRowsPerPage]);

  const handleKanbanSort = (column) => {
    if (kanbanSortBy === column) {
      setKanbanSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setKanbanSortBy(column);
      setKanbanSortDirection("asc");
    }
    setKanbanPage(0);
  };

  const handleChangeKanbanPage = (_event, newPage) => {
    setKanbanPage(newPage);
  };

  const handleChangeKanbanRowsPerPage = (event) => {
    setKanbanRowsPerPage(parseInt(event.target.value, 10));
    setKanbanPage(0);
  };

  // Configuração do react-to-print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Perfil_Cliente_${resumoData?.cliente?.NOMECLI || clienteId}`,
    pageStyle: `
      @page {
        size: A4;
        margin: 5mm 3mm; /* Reduzido para 5mm top/bottom, 3mm left/right */
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `
  });

  const formatPhone = (phone) => {
    if (!phone) return "";
    return phone.replace(/(\d{2})(\d{4,5})(\d{4})/, "+$1 ($2) $3-$4");
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return "";
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  };

  if (loading || !resumoData) {
    return (
      <Container className={classes.container}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </Container>
    );
  }

  const { cliente } = resumoData;

  // Cards de resumo do Kanban: mesmos tons do claro; no escuro, fundo translúcido + texto claro
  const totalTint = tintStyle(isDark, purple, 50, 500);
  const valorTotalTint = tintStyle(isDark, green, 50, 800);
  const valorMedioTint = tintStyle(isDark, teal, 50, 600);
  const comSemValorTint = tintStyle(isDark, orange, 50, 700);

  return (
    <Container maxWidth="lg" className={classes.container}>
      <Paper className={classes.paper}>
        {/* Informações para impressão */}
        <Box className={classes.printOnly}>
          <Typography variant="h5" style={{ fontWeight: "bold", marginBottom: 8 }}>
            PERFIL COMPLETO DO CLIENTE
          </Typography>
          <Typography variant="body2" style={{ color: "#666" }}>
            Data de Impressão: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}
          </Typography>
          <Typography variant="body2" style={{ color: "#666" }}>
            Cliente: {cliente.NOMECLI || cliente.NOMEFANTASIA}
          </Typography>
        </Box>

        {/* Cabeçalho (não imprime) */}
        <Box className={`${classes.header} ${classes.noPrint}`}>
          <Typography variant="h4" className={classes.title}>
            Perfil Completo do Cliente
          </Typography>
          <IconButton
            className={classes.printButton}
            onClick={handlePrint}
            title="Imprimir"
          >
            <PrintIcon />
          </IconButton>
        </Box>

        <Divider className={classes.divider} />

        {/* Informações da Empresa */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.subtitle}>
            <BusinessIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
            {cliente.NOMECLI || cliente.NOMEFANTASIA || "EMPRESA"}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {cliente.PESSOA && (
                <Box className={classes.infoRow}>
                  <BusinessIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Tipo:</strong> {cliente.PESSOA === 'J' ? 'Pessoa Jurídica' : cliente.PESSOA === 'F' ? 'Pessoa Física' : cliente.PESSOA}
                  </Typography>
                </Box>
              )}
              {(cliente.CIDADECLI || cliente.ESTADOCLI) && (
                <Box className={classes.infoRow}>
                  <LocationOnIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Cidade:</strong> {cliente.CIDADECLI || "-"} - {cliente.ESTADOCLI || "-"}
                  </Typography>
                </Box>
              )}
              {cliente.ENDCLI && (
                <Box className={classes.infoRow}>
                  <LocationOnIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Endereço:</strong> {cliente.ENDCLI}
                    {cliente.BAIRROCLI && `, ${cliente.BAIRROCLI}`}
                    {cliente.CEPCLI && ` - CEP: ${cliente.CEPCLI}`}
                  </Typography>
                </Box>
              )}
              {cliente.STATUS && (
                <Box className={classes.infoRow}>
                  <BusinessIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Status:</strong> {cliente.STATUS === 'A' ? 'Ativo' : cliente.STATUS === 'I' ? 'Inativo' : cliente.STATUS}
                  </Typography>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              {(cliente.FONECLI || cliente.DDDCLI) && (
                <Box className={classes.infoRow}>
                  <PhoneIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Telefone:</strong> {cliente.DDDCLI ? `(${cliente.DDDCLI}) ${cliente.FONECLI}` : cliente.FONECLI}
                  </Typography>
                </Box>
              )}
              {(cliente.CELULAR || cliente.DDDCELULAR) && (
                <Box className={classes.infoRow}>
                  <PhoneIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Celular:</strong> {cliente.DDDCELULAR ? `(${cliente.DDDCELULAR}) ${cliente.CELULAR}` : cliente.CELULAR}
                  </Typography>
                </Box>
              )}
              {cliente.CGCCLI && (
                <Box className={classes.infoRow}>
                  <BusinessIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>CNPJ:</strong> {formatCNPJ(cliente.CGCCLI)}
                  </Typography>
                </Box>
              )}
              {cliente.CPFCLI && (
                <Box className={classes.infoRow}>
                  <BusinessIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>CPF:</strong> {cliente.CPFCLI}
                  </Typography>
                </Box>
              )}
              {cliente.E_MAIL && (
                <Box className={classes.infoRow}>
                  <EmailIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>E-mail:</strong> {cliente.E_MAIL}
                  </Typography>
                </Box>
              )}
              {cliente.INSCESTCLI && (
                <Box className={classes.infoRow}>
                  <BusinessIcon className={classes.infoIcon} fontSize="small" />
                  <Typography>
                    <strong>Insc. Estadual:</strong> {cliente.INSCESTCLI}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>

        <Divider className={classes.divider} />

        {/* Histórico de Relacionamento */}
        {resumoData.historicoRelacionamento && resumoData.historicoRelacionamento.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.subtitle}>
              <HistoryIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
              Histórico de Relacionamento:
            </Typography>
            <TableContainer component={Paper} elevation={0} style={{ border: `1px solid ${isDark ? theme.palette.divider : '#e0e0e0'}` }}>
              <Table size="small">
                <TableHead className={classes.tableHeader}>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Contato</TableCell>
                    <TableCell>Meio</TableCell>
                    <TableCell>Resultado</TableCell>
                    <TableCell>Observações</TableCell>
                    <TableCell>Usuário</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {resumoData.historicoRelacionamento.map((hist) => (
                    <TableRow key={hist.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <EventIcon fontSize="small" color="action" style={{ marginRight: 4 }} />
                          {new Date(hist.data_contato).toLocaleDateString('pt-BR')}
                        </Box>
                      </TableCell>
                      <TableCell>{hist.contato?.nome || '-'}</TableCell>
                      <TableCell>
                        {hist.meio_contato && (
                          <Chip
                            label={hist.meio_contato}
                            size="small"
                            variant="outlined"
                            className={classes.chip}
                          />
                        )}
                      </TableCell>
                      <TableCell>{hist.resultado_visita || '-'}</TableCell>
                      <TableCell>
                        <Box>
                          {hist.observacoes && (
                            <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
                              <CommentIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              {hist.observacoes.length > 100 
                                ? `${hist.observacoes.substring(0, 100)}...` 
                                : hist.observacoes}
                            </Typography>
                          )}
                          {hist.potencial_vendas && (
                            <Typography variant="body2" color="primary" style={{ fontSize: '0.85rem', marginTop: 4 }}>
                              <TrendingUpIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                              Potencial: R$ {parseFloat(hist.potencial_vendas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                            </Typography>
                          )}
                          {hist.motivos && hist.motivos.length > 0 && (
                            <Box mt={0.5}>
                              {hist.motivos.map((motivo, idx) => (
                                <Chip
                                  key={idx}
                                  label={motivo.motivo}
                                  size="small"
                                  className={classes.chip}
                                  style={{ fontSize: '0.75rem' }}
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{hist.user?.name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {resumoData.historicoRelacionamento && resumoData.historicoRelacionamento.length === 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.subtitle}>
              <HistoryIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
              Histórico de Relacionamento:
            </Typography>
            <Typography color="textSecondary">Nenhum registro de histórico encontrado.</Typography>
          </Box>
        )}

        <Divider className={classes.divider} />

        {/* Contatos */}
        {cliente.contatos && cliente.contatos.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.subtitle}>
              Contato(s):
            </Typography>
            {cliente.contatos.map((contato) => (
              <Box key={contato.id} className={classes.contactItem}>
                <PersonIcon className={classes.infoIcon} />
                <Box>
                  <Typography variant="body1">
                    <strong>{contato.nome}</strong>
                    {contato.cargo && (
                      <Typography component="span" variant="caption" style={{ marginLeft: 8, fontStyle: "italic" }}>
                        ({contato.cargo})
                      </Typography>
                    )}
                  </Typography>
                  {(contato.telefone || contato.celular) && (
                    <Typography variant="body2" color="textSecondary">
                      <PhoneIcon fontSize="small" style={{ verticalAlign: "middle" }} />
                      {" "}
                      {contato.ddd_telefone && contato.telefone 
                        ? `(${contato.ddd_telefone}) ${contato.telefone}` 
                        : contato.telefone}
                      {contato.celular && (
                        <>
                          {" / "}
                          {contato.ddd_celular && contato.celular 
                            ? `(${contato.ddd_celular}) ${contato.celular}` 
                            : contato.celular}
                        </>
                      )}
                      {contato.whatsapp && (
                        <Chip
                          icon={<WhatsAppIcon />}
                          label="WhatsApp"
                          size="small"
                          color="primary"
                          className={classes.chip}
                        />
                      )}
                    </Typography>
                  )}
                  {contato.email && (
                    <Typography variant="body2" color="textSecondary">
                      <EmailIcon fontSize="small" style={{ verticalAlign: "middle" }} />
                      {" "}{contato.email}
                      {contato.email_secundario && (
                        <Typography component="span" variant="caption" style={{ marginLeft: 8 }}>
                          / {contato.email_secundario}
                        </Typography>
                      )}
                    </Typography>
                  )}
                  {contato.linkedin && (
                    <Typography variant="body2" color="textSecondary">
                      LinkedIn: {contato.linkedin}
                    </Typography>
                  )}
                  {contato.observacoes && (
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 4, fontStyle: "italic" }}>
                      {contato.observacoes}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {(!cliente.contatos || cliente.contatos.length === 0) && (
          <Box className={classes.section}>
            <Typography variant="h6" className={classes.subtitle}>
              Contato(s):
            </Typography>
            <Typography color="error">Nenhum registro encontrado!</Typography>
          </Box>
        )}

        <Divider className={classes.divider} />

        {/* Metas e Ações */}
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.subtitle}>
            Metas, Potencial e Ações de Vendas:
          </Typography>
          <TableContainer>
            <Table>
              <TableHead className={classes.tableHeader}>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Tipo Cliente</TableCell>
                  <TableCell>Segmento</TableCell>
                  <TableCell>Situação CRM</TableCell>
                  <TableCell>Limite Crédito</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{cliente.STATUS === 'A' ? 'ATIVO' : cliente.STATUS === 'I' ? 'INATIVO' : cliente.STATUS || '-'}</TableCell>
                  <TableCell>{cliente.TIPO_CLIENTE || cliente.CRM_APLICAVEL || '-'}</TableCell>
                  <TableCell>{cliente.segmento || '-'}</TableCell>
                  <TableCell>{cliente.CRM_SITUACAO_CLIENTE || '-'}</TableCell>
                  <TableCell>
                    {cliente.LIMITE 
                      ? `R$ ${parseFloat(cliente.LIMITE).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                      : cliente.LIMCREDITO 
                        ? `R$ ${parseFloat(cliente.LIMCREDITO).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` 
                        : "R$ 0,00"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Informações Adicionais de Vendas */}
          {(cliente.metas_vendas || cliente.potencial_vendas || cliente.acoes_vendas) && (
            <Box mt={2}>
              {cliente.metas_vendas && (
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Metas de Vendas:</strong> {cliente.metas_vendas}
                  </Typography>
                </Box>
              )}
              {cliente.potencial_vendas && (
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Potencial de Vendas:</strong> {cliente.potencial_vendas}
                  </Typography>
                </Box>
              )}
              {cliente.acoes_vendas && (
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Ações de Vendas:</strong> {cliente.acoes_vendas}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

        </Box>


        {/* Seção de Pedidos */}
        {resumoData?.pedidos && (
          <>
            <Box className={classes.section}>
              <Typography variant="h6" className={classes.subtitle}>
                Histórico de Pedidos
              </Typography>
              
              {/* Resumo de Pedidos */}
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} md={3}>
                  <Box className={classes.addressBox}>
                    <Typography variant="body2" color="textSecondary">Total de Pedidos</Typography>
                    <Typography variant="h6">{resumoData.pedidos.total || 0}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box className={classes.addressBox}>
                    <Typography variant="body2" color="textSecondary">Valor Total</Typography>
                    <Typography variant="h6">
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(resumoData.pedidos.valor_total || 0)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box className={classes.addressBox}>
                    <Typography variant="body2" color="textSecondary">Primeira Compra</Typography>
                    <Typography variant="body1">
                      {resumoData.pedidos.primeira_compra 
                        ? new Date(resumoData.pedidos.primeira_compra.data_pedido).toLocaleDateString('pt-BR')
                        : '-'}
                    </Typography>
                    {resumoData.pedidos.primeira_compra && (
                      <Typography variant="caption" color="textSecondary">
                        {resumoData.pedidos.primeira_compra.numero_pedido}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box className={classes.addressBox}>
                    <Typography variant="body2" color="textSecondary">Última Compra</Typography>
                    <Typography variant="body1">
                      {resumoData.pedidos.ultima_compra 
                        ? new Date(resumoData.pedidos.ultima_compra.data_pedido).toLocaleDateString('pt-BR')
                        : '-'}
                    </Typography>
                    {resumoData.pedidos.ultima_compra && (
                      <Typography variant="caption" color="textSecondary">
                        {resumoData.pedidos.ultima_compra.numero_pedido}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Tabela de Pedidos */}
              {resumoData.pedidos.lista && resumoData.pedidos.lista.length > 0 && (
                <TableContainer component={Paper} variant="outlined" style={{ marginTop: 16 }}>
                  <Table size="small">
                    <TableHead className={classes.tableHeader}>
                      <TableRow>
                        <TableCell><strong>Pedido</strong></TableCell>
                        <TableCell><strong>Data</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell align="right"><strong>Valor</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resumoData.pedidos.lista.slice(0, 10).map((pedido) => (
                        <TableRow key={pedido.id}>
                          <TableCell>{pedido.numero_pedido}</TableCell>
                          <TableCell>{new Date(pedido.data_pedido).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>
                            <Chip 
                              label={pedido.status} 
                              size="small"
                              style={{ 
                                backgroundColor: 
                                  pedido.status === 'entregue' ? '#4caf50' :
                                  pedido.status === 'enviado' ? '#9c27b0' :
                                  pedido.status === 'em_producao' ? '#ff9800' :
                                  pedido.status === 'confirmado' ? '#2196f3' :
                                  pedido.status === 'cancelado' ? '#f44336' : '#9e9e9e',
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pedido.valor_total || 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>

            <Divider className={classes.divider} />

            {/* Produtos Mais Comprados */}
            {resumoData.pedidos.produtos_mais_comprados && resumoData.pedidos.produtos_mais_comprados.length > 0 && (
              <>
                <Box className={classes.section}>
                  <Typography variant="h6" className={classes.subtitle}>
                    Relação dos Produtos que Mais Compra (Limitado aos 25 Primeiros Colocados)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead className={classes.tableHeader}>
                        <TableRow>
                          <TableCell><strong>Produto</strong></TableCell>
                          <TableCell align="right"><strong>Menor Valor</strong></TableCell>
                          <TableCell align="right"><strong>Maior Valor</strong></TableCell>
                          <TableCell align="right"><strong>Valor Médio</strong></TableCell>
                          <TableCell align="center"><strong>Qtd.</strong></TableCell>
                          <TableCell align="right"><strong>Valor Total</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resumoData.pedidos.produtos_mais_comprados.map((produto, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {produto.descricao}
                              {produto.codigo_produto && (
                                <Typography variant="caption" display="block" color="textSecondary">
                                  Cód: {produto.codigo_produto}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(produto.menor_valor)}
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(produto.maior_valor)}
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(produto.valor_total / produto.quantidade_total)}
                            </TableCell>
                            <TableCell align="center">
                              {new Intl.NumberFormat('pt-BR').format(produto.quantidade_total)}
                            </TableCell>
                            <TableCell align="right">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(produto.valor_total)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Divider className={classes.divider} />
              </>
            )}
          </>
        )}

        {/* Atendimentos do Kanban */}
        {resumoData.kanbanAtendimentos && resumoData.kanbanAtendimentos.total_cards > 0 && (
          <>
            <Divider className={classes.divider} />
            <Box className={classes.section}>
              <Typography variant="h6" className={classes.subtitle}>
                <DashboardIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
                Atendimentos (Kanban)
              </Typography>

              {/* Cards de Resumo */}
              <Grid container spacing={2} style={{ marginBottom: 16 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className={classes.addressBox} style={{ backgroundColor: totalTint.backgroundColor, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">Total de Atendimentos</Typography>
                    <Typography variant="h4" style={{ fontWeight: 'bold', color: totalTint.color }}>
                      {resumoData.kanbanAtendimentos.total_cards}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className={classes.addressBox} style={{ backgroundColor: valorTotalTint.backgroundColor, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">Valor Total</Typography>
                    <Typography variant="h5" style={{ fontWeight: 'bold', color: valorTotalTint.color }}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(resumoData.kanbanAtendimentos.valor_total)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className={classes.addressBox} style={{ backgroundColor: valorMedioTint.backgroundColor, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">Valor Médio</Typography>
                    <Typography variant="h5" style={{ fontWeight: 'bold', color: valorMedioTint.color }}>
                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(resumoData.kanbanAtendimentos.valor_medio)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box className={classes.addressBox} style={{ backgroundColor: comSemValorTint.backgroundColor, textAlign: 'center' }}>
                    <Typography variant="caption" color="textSecondary">Com Valor / Sem Valor</Typography>
                    <Typography variant="h5" style={{ fontWeight: 'bold', color: comSemValorTint.color }}>
                      {resumoData.kanbanAtendimentos.cards_com_valor} / {resumoData.kanbanAtendimentos.cards_sem_valor}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginTop: 16, marginBottom: 8 }}>
                Cards do Kanban
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead className={classes.tableHeader}>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={kanbanSortBy === "boardName"}
                          direction={kanbanSortBy === "boardName" ? kanbanSortDirection : "asc"}
                          onClick={() => handleKanbanSort("boardName")}
                          style={{ color: "inherit" }}
                        >
                          <strong>Board</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={kanbanSortBy === "statusFluxo"}
                          direction={kanbanSortBy === "statusFluxo" ? kanbanSortDirection : "asc"}
                          onClick={() => handleKanbanSort("statusFluxo")}
                          style={{ color: "inherit" }}
                        >
                          <strong>Status</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={kanbanSortBy === "title"}
                          direction={kanbanSortBy === "title" ? kanbanSortDirection : "asc"}
                          onClick={() => handleKanbanSort("title")}
                          style={{ color: "inherit" }}
                        >
                          <strong>Título</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={kanbanSortBy === "assignedUserName"}
                          direction={kanbanSortBy === "assignedUserName" ? kanbanSortDirection : "asc"}
                          onClick={() => handleKanbanSort("assignedUserName")}
                          style={{ color: "inherit" }}
                        >
                          <strong>Responsável</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={kanbanSortBy === "valor"}
                          direction={kanbanSortBy === "valor" ? kanbanSortDirection : "asc"}
                          onClick={() => handleKanbanSort("valor")}
                          style={{ color: "inherit" }}
                        >
                          <strong>Valor</strong>
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center"><strong>Acessar</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kanbanCardsLoading ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" py={2}>
                            <CircularProgress size={22} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : sortedKanbanCards.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          Nenhum card encontrado para este cliente
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedKanbanCards.map((card) => {
                        const canAccess = Boolean(card?.id && card?.boardId && card?.columnId);

                        return (
                          <TableRow key={card.id} hover>
                            <TableCell>{card.boardName || "-"}</TableCell>
                            <TableCell>
                              <Chip
                                label={card.statusFluxo || "-"}
                                size="small"
                                style={{ backgroundColor: isDark ? alpha(blue[500], 0.2) : '#e3f2fd' }}
                              />
                            </TableCell>
                            <TableCell>{card.title || "-"}</TableCell>
                            <TableCell>{card.assignedUserName || "-"}</TableCell>
                            <TableCell>{formatCurrency(card.valor)}</TableCell>
                            <TableCell align="center">
                              <Tooltip title={canAccess ? "Abrir card no Kanban" : "Card sem destino válido"}>
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!canAccess}
                                    onClick={() => handleOpenKanbanCard(card)}
                                  >
                                    <OpenInNewIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={sortedKanbanCards.length}
                page={kanbanPage}
                onChangePage={handleChangeKanbanPage}
                rowsPerPage={kanbanRowsPerPage}
                onChangeRowsPerPage={handleChangeKanbanRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="Itens por página"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </Box>
          </>
        )}

        <Divider className={classes.divider} />

        {/* Observações e Anotações */}
        {(cliente.OBS || cliente.OBSCLI || cliente.ANOTACAO) && (
          <>
            <Box className={classes.section}>
              <Typography variant="h6" className={classes.subtitle}>
                Observações e Anotações
              </Typography>
              {cliente.OBS && (
                <Box className={classes.addressBox} mb={2}>
                  <Typography variant="body2">
                    <strong>Observações:</strong>
                  </Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                    {cliente.OBS}
                  </Typography>
                </Box>
              )}
              {cliente.OBSCLI && (
                <Box className={classes.addressBox} mb={2}>
                  <Typography variant="body2">
                    <strong>Observações do Cliente:</strong>
                  </Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                    {cliente.OBSCLI}
                  </Typography>
                </Box>
              )}
              {cliente.ANOTACAO && (
                <Box className={classes.addressBox} mb={2}>
                  <Typography variant="body2">
                    <strong>Anotações:</strong>
                  </Typography>
                  <Typography variant="body2" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                    {cliente.ANOTACAO}
                  </Typography>
                </Box>
              )}
            </Box>
            <Divider className={classes.divider} />
          </>
        )}

      </Paper>

      {/* Template de impressão (oculto) */}
      <div style={{ display: 'none' }}>
        <ClienteResumoPrintTemplate 
          ref={printRef} 
          cliente={cliente} 
          pedidos={resumoData?.pedidos}
          historicoRelacionamento={resumoData?.historicoRelacionamento}
        />
      </div>
    </Container>
  );
};

export default ClienteResumo;
